<template>
  <Section
    :id="id"
    :header="t('common.locationsAndDates')"
    class="location-section"
  >
    <template #headerRight>
      <LocationMarkers :origin="origin" :destination="destination" />

      <div v-if="!showLimitedLocation" class="flex justify-content-end w-4">
        <ActionButton mode="secondary" :actions="actions" />
      </div>
    </template>

    <div
      v-for="(stop, i) in internalValue"
      :key="stop.id"
      class="location-container"
    >
      <LocationTracker
        :stop-type="stop.stopType"
        :sequence-number="stop.sequenceNumber"
        :mileage="getCurrentStopMileage(internalValue[i])"
        :has-next-stop="i < internalValue.length - 1"
        :has-error="!!mileages.error && i < internalValue.length - 1"
      />

      <component
        :is="showLimitedLocation ? LocationCardLimited : LocationCard"
        :id="`${stop.id}-location-card`"
        v-model:expanded="expansionStates[i]"
        :location="stop"
        :parent-entity="parentEntity"
        :pick-drop-type="pickDropType[i]"
        :line-items="getLineItemsForStop(stop)"
        :pick-drop-sequence-number="pickDropSequence[i]"
        :accessorial-codes="accessorialCodes"
        :require-both-dates="requireBothDates"
        :editable="editable"
        :hide-notes="hideNotes"
        :is-authenticated-user="isAuthenticatedUser"
        @update:location="onUpdateLocation($event, i)"
      />
    </div>

    <InputError
      v-for="error of errors"
      :id="error.$uid"
      :key="error.$uid"
      :error="error"
    />

    <StopEdit
      v-if="shipment && newStop"
      id="add-stop"
      v-model="newStop"
      class="add-stop-entry"
      :stops="shipment.stops"
      allow-changing-stop-type
      show-add-to-shipment-buttons
      @add-to-shipment="onAddStopConfirm"
      @cancel="onCancelAddStop"
    />

    <MultistopArrangeableStopListModal
      v-if="shipment"
      v-model:visible="isRearrangeStopsModalVisible"
      :model-value="shipment"
      class="max-w-full"
      @update:model-value="onConfirmRearrangeStops"
    />

    <template v-if="canAddStops && !newStop" #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.addParam', { param: t('shipments.stops') })"
        icon="pi pi-plus"
        @click="onAddStop"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDirtyState, useValidations } from '@rygen/rygen-ui/composables';
import {
  AccessorialCode,
  Loadable,
  Optional,
  RygenMenuItem,
} from '@rygen/rygen-ui/model';

import {
  LineItem,
  Order,
  Shipment,
  ShipmentLineItem,
  ShipmentStop,
  ShipmentStopType,
  ShippingPoint,
  StopMileageResponse,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { ParentEntity } from '@/stores/ParentEntity';

import StopEdit from '@/components/entry/StopEdit.vue';
import LocationCard from '@/components/location/LocationCard.vue';
import LocationCardLimited from '@/components/location/LocationCardLimited.vue';
import LocationMarkers from '@/components/location/LocationMarkers.vue';
import LocationTracker from '@/components/location/LocationTracker.vue';
import MultistopArrangeableStopListModal from '@/components/location/MultistopArrangeableStopListModal.vue';
import ActionButton from '@rygen/rygen-ui/components/button/ActionButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputError from '@rygen/rygen-ui/components/form/InputError.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const internalValue = defineModel({
  type: Array as PropType<ShippingPoint[]>,
  default: () => [],
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  parentEntity: {
    type: Object as PropType<ParentEntity>,
    default: () => new Order(),
  },
  mileages: {
    type: Object as PropType<Loadable<StopMileageResponse[]>>,
    default: () => ({ loading: false }),
  },
  lineItems: {
    type: Array as PropType<LineItem[]>,
    default: () => [],
  },
  /** An array of vuelidate errors for the stops array as a whole. */
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  accessorialCodes: {
    type: Object as PropType<Loadable<AccessorialCode[]>>,
    default: () => ({ loading: false }),
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
  hideNotes: {
    type: Boolean,
    default: () => false,
  },
  showLimitedLocation: {
    type: Boolean,
    default: () => false,
  },
  canAddStops: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  parentEntity,
  mileages,
  lineItems,
  errors,
  accessorialCodes,
  editable,
  hideNotes,
  showLimitedLocation,
  canAddStops,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'change', payload: { isDirty: boolean; numDirty: number }): void;
  (event: 'update:line-items', lineItems: ShipmentLineItem[]): void;
}>();

defineExpose({
  reset: () => {
    nextTick(() => {
      resetDirtyState(internalValue.value);
      emit('change', dirtyState.value);
    });
  },
});

const { isAuthenticatedUser } = useCurrentUserStore();
const { dirtyState, itemEdited, resetDirtyState } = useDirtyState(
  internalValue.value,
);
const { t } = useI18n();
const { validate } = useValidations(internalValue, {});

const isRearrangeStopsModalVisible = ref(false);
const newStop: Ref<Optional<ShipmentStop>> = ref();
const expansionStates: Ref<boolean[]> = ref(
  internalValue.value.map(() => false),
);

const shipment = computed(() => {
  return parentEntity.value instanceof Shipment
    ? parentEntity.value
    : undefined;
});

const requireBothDates = computed(() => {
  return shipment.value?.requireBothArrivalAndDepartureOnStops ?? false;
});

const origin = computed(() => internalValue.value[0]);

const destination = computed(
  () => internalValue.value[internalValue.value.length - 1],
);

const areAllExpanded = computed(() => {
  return expansionStates.value.every((isExpanded) => isExpanded);
});

const isAnyExpanded = computed(() => {
  return expansionStates.value.some((isExpanded) => isExpanded);
});

const actions: Ref<RygenMenuItem[]> = computed(() => [
  {
    key: 'EXPAND_ALL',
    label: t('common.expandAll'),
    icon: 'pi pi-expand',
    command: () => {
      expansionStates.value = internalValue.value.map(() => true);
    },
    visible: !areAllExpanded.value,
  },
  {
    key: 'COLLAPSE_ALL',
    label: t('common.collapseAll'),
    icon: 'pi pi-expand',
    command: () => {
      expansionStates.value = internalValue.value.map(() => false);
    },
    visible: isAnyExpanded.value,
  },
  {
    key: 'REARRANGE',
    label: t('multistop.rearrangeStops'),
    icon: 'pi pi-sort-alt',
    command: () => {
      isRearrangeStopsModalVisible.value = true;
    },
    visible: editable.value && internalValue.value.length > 2,
  },
]);

const pickDropSequence = computed((): number[] => {
  let picks = 0;
  let drops = 0;

  return internalValue.value.map((stop) => (stop.isPickup ? ++picks : ++drops));
});

const pickDropType = computed(() => {
  return internalValue.value.map((s, index, arr) => {
    if (index === 0) {
      return 'origin';
    } else if (index === arr.length - 1) {
      return 'destination';
    } else if (!(s instanceof ShipmentStop)) {
      console.warn(
        'How did this happen!? Orders should only have 2 stops',
        arr,
      );
      return 'origin';
    } else {
      return s.stopType === ShipmentStopType.PICKUP ? 'pickup' : 'dropoff';
    }
  });
});

const onConfirmRearrangeStops = (updatedShipment: Shipment) => {
  internalValue.value = updatedShipment.stops;
  // Rearranging stops corrects the sequence numbers on line items; forward those
  // updated line items so the parent shipment model stays in sync.
  emit('update:line-items', updatedShipment.lineItems);
};

const getLineItemsForStop = (stop: ShippingPoint) => {
  if (!(stop instanceof ShipmentStop)) {
    return lineItems.value;
  }

  return lineItems.value.filter((lineItem) => {
    return (
      lineItem instanceof ShipmentLineItem &&
      (lineItem.pickupStopId === stop.id || lineItem.dropOffStopId === stop.id)
    );
  });
};

const getCurrentStopMileage = (shippingPoint: ShippingPoint) => {
  const sequenceNumber = shippingPoint.sequenceNumber ?? 0;

  const mileageForLeg = mileages.value.data?.find(
    (r) => r.originSequenceNumber === sequenceNumber,
  );
  return mileageForLeg?.mileage;
};

const onUpdateLocation = (newVal: ShippingPoint, index: number) => {
  internalValue.value.splice(index, 1, newVal);
  itemEdited(index, true);
  emit('change', dirtyState.value);
};

const onAddStop = () => {
  newStop.value = new ShipmentStop({ stopType: ShipmentStopType.PICKUP });
};

const onAddStopConfirm = async () => {
  if (!(await validate())) {
    return;
  }

  const stop = new ShipmentStop({
    ...newStop.value,
    sequenceNumber: internalValue.value.length + 1,
  });
  internalValue.value.push(stop);
  newStop.value = undefined;
  emit('change', { isDirty: true, numDirty: dirtyState.value.numDirty + 1 });
};

const onCancelAddStop = () => {
  newStop.value = undefined;
};

watch(
  internalValue,
  (newVal: ShippingPoint[]) => {
    if (isEqual(newVal, internalValue)) {
      return;
    }

    expansionStates.value = internalValue.value.map(() => false);
  },
  { immediate: true },
);
</script>

<style>
.location-section {
  --location-tracker-icon-margin-top: var(
    --location-card-top-row-cell-padding-top
  );

  .section-header {
    /*
    Same height as action button when it has a dropdown menu. This keeps things
    from jumping around when the button's height increases when it goes from a
    simple button to a dropdown button.
    */
    height: var(--input-height);

    .section-header-right {
      gap: var(--gap-2);
      justify-content: flex-end;
      width: 50%;

      .location-markers {
        flex: 1 0 auto;
        justify-content: flex-end;
      }
    }
  }

  .section-data {
    display: flex;
    flex-flow: column;
    gap: var(--sp-6);

    .location-container {
      display: inline-flex;
      gap: var(--gap-2);
      min-width: 100%;

      .location-tracker {
        flex: 0 0 auto;
        width: min-content;
        margin-left: calc(var(--sp-3) * -1);
      }

      .location-card {
        flex: 1 0 auto;
        /*
          This is to keep the card from overflowing. Why does this work? I don't
          know. CSS is weird
        */
        width: 0;
      }
    }

    .add-stop-entry {
      margin-left: var(--sp-8);
    }
  }

  .section-footer {
    margin-top: var(--gap-1);
    margin-left: var(--sp-8);
  }
}
</style>
